import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Spinner, Nav, Tab } from 'react-bootstrap';
import axios from 'axios';
import { useAuth } from '../AuthContext';

// Import tabs
import OverviewTab from './analysis/OverviewTab';
import CompletionAnalysisTab from './analysis/CompletionAnalysisTab';
import InvitationsTab from './analysis/InvitationsTab';
import ResponseDetailsTab from './analysis/ResponseDetailsTab';

// Import utility
import { calculateStats } from '../utils/statsCalculator';

const API_URL = process.env.REACT_APP_API_URL;

const SurveyAnalysis = () => {
    const [responses, setResponses] = useState([]);
    const [survey, setSurvey] = useState(null);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        totalResponses: 0,
        submittedResponses: 0,
        incompleteResponses: 0,
        questionsWithFollowups: {},
        averageFollowupsPerQuestion: 0,
        responsesByDate: [],
        completionRate: 0,
        averageQuestionsAnswered: 0,
        abandonmentPoints: {},
    });

    const [invitationStats, setInvitationStats] = useState({
        totalInvitations: 0,
        startedCount: 0,
        completedCount: 0,
        startRate: 0,
        completionRate: 0
    });

    const { id } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);

                const idToken = await user.getIdToken();
                const headers = { Authorization: `Bearer ${idToken}` };

                // First get survey data to get the shortcode
                const surveyRes = await axios.get(`${API_URL}/api/surveys/${id}`, { headers });
                const surveyData = surveyRes.data.data;
                setSurvey(surveyData);

                // Then fetch responses and invitation stats in parallel
                const [responsesRes, invitationStatsRes] = await Promise.all([
                    axios.get(`${API_URL}/api/surveys/${id}/responses`, { headers }),
                    axios.get(`${API_URL}/api/surveys/${surveyData.shortcode}/invitation-stats`, { headers })
                        .catch(err => {
                            console.warn('Error fetching invitation stats:', err);
                            return { data: { data: {
                                        totalInvitations: 0,
                                        startedCount: 0,
                                        completedCount: 0,
                                        startRate: 0,
                                        completionRate: 0
                                    }}};
                        })
                ]);

                setResponses(responsesRes.data.data);
                setInvitationStats(invitationStatsRes.data.data);

                // Calculate statistics
                const calculatedStats = calculateStats(responsesRes.data.data, surveyData);
                setStats(calculatedStats);

                setLoading(false);
            } catch (error) {
                console.error('Error fetching analysis data:', error);
                setLoading(false);
            }
        };

        fetchData();
    }, [id, user]);

    // Filter responses with email (invitation responses)
    const getInvitationResponses = () => {
        if (!responses) return [];

        // Filter responses that have an email field (these are invitation responses)
        return responses.filter(response => response.email);
    };

    if (loading) {
        return (
            <div className="text-center mt-5">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </div>
        );
    }

    return (
        <div className="container mt-4">
            <h2>{survey?.title} - Analysis</h2>

            <Tab.Container id="analysis-tabs" defaultActiveKey="overview">
                <Nav variant="tabs" className="mb-3">
                    <Nav.Item>
                        <Nav.Link eventKey="overview">Overview</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="completion">Completion Analysis</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="invitations">Invitations</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="responses">Response Details</Nav.Link>
                    </Nav.Item>
                </Nav>

                <Tab.Content>
                    <Tab.Pane eventKey="overview">
                        <OverviewTab
                            stats={stats}
                            invitationStats={invitationStats}
                            survey={survey}
                        />
                    </Tab.Pane>

                    <Tab.Pane eventKey="completion">
                        <CompletionAnalysisTab
                            stats={stats}
                            survey={survey}
                        />
                    </Tab.Pane>

                    <Tab.Pane eventKey="invitations">
                        <InvitationsTab
                            invitationStats={invitationStats}
                            loading={loading}
                            getInvitationResponses={getInvitationResponses}
                        />
                    </Tab.Pane>

                    <Tab.Pane eventKey="responses">
                        <ResponseDetailsTab
                            responses={responses}
                            survey={survey}
                        />
                    </Tab.Pane>
                </Tab.Content>
            </Tab.Container>
        </div>
    );
};

export default SurveyAnalysis;